'use client'

import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Category } from '@/payload-types'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import { CategoryPill } from './CategoryPill'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/utilities/cn'

interface CategoryPillsProps {
  categories: Category[]
  recentCategories?: number[]
  showHierarchy?: boolean
  maxDepth?: number
}

interface CategoryWithChildren extends Category {
  children?: CategoryWithChildren[]
}

export const CategoryPills: React.FC<CategoryPillsProps> = ({
  categories,
  // recentCategories = [],
  showHierarchy = true,
  maxDepth = 4,
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set())

  // Get active category from URL
  const activeCategoryId = useMemo(() => {
    const categoryParam = searchParams.get('category')
    return categoryParam ? parseInt(categoryParam) : null
  }, [searchParams])

  // Build category hierarchy tree
  const categoryTree = useMemo(() => {
    const categoryMap = new Map<number, CategoryWithChildren>()
    const rootCategories: CategoryWithChildren[] = []

    // First pass: create map of all categories
    categories.forEach((cat) => {
      categoryMap.set(cat.id, { ...cat, children: [] })
    })

    // Second pass: build tree structure
    categories.forEach((cat) => {
      const category = categoryMap.get(cat.id)!
      const parentId = typeof cat.parent === 'object' && cat.parent ? cat.parent.id : cat.parent

      if (parentId && categoryMap.has(parentId)) {
        const parent = categoryMap.get(parentId)!
        if (!parent.children) parent.children = []
        parent.children.push(category)
      } else {
        rootCategories.push(category)
      }
    })

    return rootCategories
  }, [categories])

  // Auto-expand active category's parents
  useEffect(() => {
    if (activeCategoryId) {
      const findParents = (catId: number): number[] => {
        const category = categories.find((c) => c.id === catId)
        if (!category) return []

        const parentId =
          typeof category.parent === 'object' && category.parent
            ? category.parent.id
            : category.parent

        if (parentId) {
          return [parentId, ...findParents(parentId)]
        }
        return []
      }

      const parents = findParents(activeCategoryId)
      setExpandedCategories(new Set([...parents, activeCategoryId]))
    }
  }, [activeCategoryId, categories])

  const handleCategoryClick = useCallback(
    (categoryId: number) => {
      const params = new URLSearchParams(searchParams.toString())

      // Toggle category selection
      if (activeCategoryId === categoryId) {
        params.delete('category')
        setExpandedCategories(new Set())
      } else {
        params.set('category', String(categoryId))

        // Toggle expansion for categories with children
        const category = categories.find((c) => c.id === categoryId)
        const hasChildren = categories.some((c) => {
          const parentId = typeof c.parent === 'object' && c.parent ? c.parent.id : c.parent
          return parentId === categoryId
        })

        if (hasChildren) {
          const newExpanded = new Set(expandedCategories)
          if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId)
          } else {
            newExpanded.add(categoryId)
          }
          setExpandedCategories(newExpanded)
        }

        // Store in localStorage for recently viewed
        try {
          const recent = JSON.parse(localStorage.getItem('recentCategories') || '[]')
          const updated = [categoryId, ...recent.filter((id: number) => id !== categoryId)].slice(
            0,
            5,
          )
          localStorage.setItem('recentCategories', JSON.stringify(updated))
        } catch (e) {
          console.error('Failed to save recent categories:', e)
        }
      }

      const newParams = params.toString()
      router.push(pathname + (newParams ? '?' + newParams : ''))
    },
    [activeCategoryId, categories, expandedCategories, pathname, router, searchParams],
  )

  // Check if any descendant of a category is active
  const isDescendantActive = (categoryId: number): boolean => {
    if (activeCategoryId === categoryId) return true

    const children = categories.filter((c) => {
      const parentId = typeof c.parent === 'object' && c.parent ? c.parent.id : c.parent
      return parentId === categoryId
    })

    return children.some((child) => isDescendantActive(child.id))
  }

  // Find the deepest expanded depth
  const getDeepestExpandedDepth = (cat: CategoryWithChildren, currentDepth: number = 0): number => {
    if (!expandedCategories.has(cat.id) || !cat.children || cat.children.length === 0) {
      return currentDepth
    }

    const childDepths = cat.children.map((child) =>
      getDeepestExpandedDepth(child, currentDepth + 1),
    )
    return Math.max(currentDepth, ...childDepths)
  }

  // Recursive render for category hierarchy
  const renderCategory = (
    category: CategoryWithChildren,
    depth: number = 0,
    rootCategory?: CategoryWithChildren,
  ) => {
    if (depth >= maxDepth) return null

    const root = rootCategory || category
    const deepestDepth = getDeepestExpandedDepth(root)
    const isActive = activeCategoryId === category.id
    const hasActiveDescendant = isDescendantActive(category.id)
    const isExpanded = expandedCategories.has(category.id)
    const hasChildren = category.children && category.children.length > 0
    const isDeepestExpanded = depth === deepestDepth && depth > 0

    return (
      <React.Fragment key={category.id}>
        <CategoryPill
          category={category}
          isActive={hasActiveDescendant}
          isExactMatch={isActive}
          hasChildren={hasChildren}
          depth={depth}
          isDeepestExpanded={isDeepestExpanded}
          onClick={handleCategoryClick}
          showChildrenIndicator={showHierarchy}
        />
        {showHierarchy && hasChildren && isExpanded && (
          <AnimatePresence>
            <motion.div
              className="flex flex-wrap gap-2 ml-4 mt-2 w-full max-w-full"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{
                duration: 0.25,
                ease: 'easeInOut',
                height: { duration: 0.25 },
                opacity: { duration: 0.2 },
              }}
            >
              {category.children!.map((child) => renderCategory(child, depth + 1, root))}
            </motion.div>
          </AnimatePresence>
        )}
      </React.Fragment>
    )
  }

  // Only separate root-level categories - children will render within their parents
  const activeRootCategories: CategoryWithChildren[] = []
  const inactiveRootCategories: CategoryWithChildren[] = []

  categoryTree.forEach((category) => {
    const hasActiveDescendant = isDescendantActive(category.id)

    if (hasActiveDescendant) {
      activeRootCategories.push(category)
    } else {
      inactiveRootCategories.push(category)
    }
  })

  return (
    <div className="w-full" data-testid="category-pills">
      <div
        className={cn(
          'flex flex-col gap-2',
          // Prevent overflow issues
          'max-w-full',
        )}
      >
        {/* Show inactive pills first */}
        <div className="flex flex-wrap gap-2">
          {inactiveRootCategories.map((category) => renderCategory(category, 0))}
        </div>
        {/* Show active/expanded pills at the bottom */}
        {activeRootCategories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeRootCategories.map((category) => renderCategory(category, 0))}
          </div>
        )}
      </div>
    </div>
  )
}
